import { useDispatch, useSelector } from 'react-redux';
import { STEPS, setPlan } from '@/store/checkout';
import { useEffect, useRef } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getVatValues } from '@/modules/price';
import { useShallowEqualSelector } from '@/hooks';
import { PLAN_TYPE } from '@/const/plans';
import { getMainPlan, getPriceByIdAndCurrency } from '@/utils/price';
import { TRIAL_DAYS } from '@/config';

export { STEPS };

export function usePlan() {
	const dispatch = useDispatch();

	const {
		initializing,
		ready,
		prices,
		priceId,
		relevantBillingData,
		currency,
		fetchingPrices,
		discount
	} = useShallowEqualSelector(({ auth, checkout }) => ({
		initializing: checkout.initCheckout?.loading,
		ready: checkout.initCheckout?.success,
		prices: checkout.prices,
		priceId: checkout.priceId,
		relevantBillingData: checkout.relevantBillingData,
		currency: auth.currency || checkout.priceCurrency,
		fetchingPrices: checkout.getPrices?.loading,
		discount: auth?.user?.discount
	}));

	const { country, accountType } = relevantBillingData || {};

	const currentPlan = getPriceByIdAndCurrency(priceId, currency, prices) || {};

	if (priceId && currentPlan?.handlerId && priceId !== currentPlan.handlerId) {
		dispatch(setPlan(currentPlan.handlerId));
	}

	const monthlyPlan = prices.find(
		price => price.active && price.name === PLAN_TYPE.MONTHLY
	);

	const trialPlan = prices.find(
		price => price.active && price.name === PLAN_TYPE.TRIAL
	);

	const mainPlan = getMainPlan(prices);

	const vatInfo = getVatValues(currentPlan, {
		country,
		accountType
	});

	return {
		initializing,
		ready,
		prices,
		priceId,
		setPriceId: id => dispatch(setPlan(id)),
		currentPlan,
		vatInfo,
		currency,
		fetchingPrices,
		discount,
		monthlyPlan,
		trialPlan,
		mainPlan
	};
}

export function usePlansMapping() {
	const { t } = useTranslation();

	const { prices = [] } = useSelector(state => state.checkout);

	return prices.reduce((acc, price) => {
		acc[price.name] = {
			name: t(`plans.${price.name}.name`, { trialDays: TRIAL_DAYS })
		};
		return acc;
	}, {});
}

export function useMapPlanName(name) {
	const plansMapping = usePlansMapping();

	return plansMapping?.[name] || '';
}

export function useScrollToPayment() {
	const paymentRef = useRef(null);
	const { step } = useSelector(state => state.checkout);
	const theme = useTheme();
	const smDown = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });

	useEffect(() => {
		if (smDown && step === STEPS.PAYMENT && paymentRef.current) {
			paymentRef.current.scrollIntoView({
				block: 'center'
			});
		}
	}, [step]);

	return paymentRef;
}
